import {
  Component,
  inject,
  ChangeDetectorRef,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { SlideInRightModalComponent } from '@shared/ui/slide-in-right-modal/slide-in-right-modal.component';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { InputComponent } from '@shared/ui/input/input.component';
import { ButtonDirective, SpinDirective } from '@shared/directives/button.directive';
import { AccountManagementService } from '@core/services/account-management.service';
import { NotificationFacade } from '@core/facades/notification.facade';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-nin-minor-account',
  standalone: true,
  imports: [
    CommonModule,
    SlideInRightModalComponent,
    FormsModule,
    ReactiveFormsModule,
    InputLabelComponent,
    InputComponent,
    ButtonDirective,
    SpinDirective,
  ],
  templateUrl: './nin-minor-account.component.html',
  styleUrls: ['./nin-minor-account.component.scss'],
})
export class NinMinorAccountComponent implements OnInit, OnDestroy {
  subs: Subscription[] = [];
  returnPathUrl: string = '';
  isLoading: boolean = false;
  isCheckingEmail: boolean = false;
  accountId!: string;
  accountHolderEmail!: string;

  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private toast = inject(NotificationFacade);
  private change = inject(ChangeDetectorRef);
  private accountService = inject(AccountManagementService);

  minorForm = this.fb.nonNullable.group({
    nin: ['', Validators.required],
    first_name: ['', Validators.required],
    last_name: ['', Validators.required],
    dob: ['', Validators.required],
    email: [''],
  });

  ngOnInit(): void {
    this.accountId =
      this.activatedRoute.snapshot.parent?.params['accountId'];

      this.setupEmailValidation();
      this.fetchAccountHolderEmail();
  }

  private setupEmailValidation(): void {
    const emailControl = this.minorForm.get('email');

    if (emailControl) {
      const emailSub = emailControl.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
        )
        .subscribe((email) => {
          // Clear previous errors
          const currentErrors = emailControl.errors;
          if (currentErrors) {
            delete currentErrors['emailExists'];
            delete currentErrors['serverError'];
            emailControl.setErrors(
              Object.keys(currentErrors).length > 0 ? currentErrors : null
            );
          }

          if (email && email.trim()) {
            this.checkEmailDuplicate(email);
          }
        });

      this.subs.push(emailSub);
    }
  }

  private checkEmailDuplicate(email: string): void {
    this.isCheckingEmail = true;
    this.change.detectChanges();

    const checkSub = this.accountService
      .checkUserDetail(email, undefined)
      .subscribe({
        next: (response) => {
          this.isCheckingEmail = false;
          const emailControl = this.minorForm.get('email');

          if (response.exists && emailControl) {
            emailControl.setErrors({
              ...(emailControl.errors || {}),
              emailExists: true,
            });
          }

          this.change.detectChanges();
        },
        error: () => {
          this.isCheckingEmail = false;
          const emailControl = this.minorForm.get('email');

          if (emailControl) {
            emailControl.setErrors({
              ...(emailControl.errors || {}),
              serverError: true,
            });
          }

          this.change.detectChanges();
        },
      });

    this.subs.push(checkSub);
  }

  private fetchAccountHolderEmail(): void {
    const sub = this.accountService.getIndividualAccountInfo(this.accountId).subscribe({
      next: (response) => {
        this.accountHolderEmail = response.data.user.email;
      },
      error: () => {
        this.toast.error('Failed to fetch account holder email');
      },
    });

    this.subs.push(sub);
  }

  get minDate(): string {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 18);
    return date.toISOString().split('T')[0];
  }

  get maxDate(): string{
    const date = new Date();
    date.setFullYear(date.getFullYear());
    return date.toISOString().split('T')[0];
  }

  submit(): void {
    if (this.minorForm.invalid) {
      this.minorForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const formData = this.minorForm.getRawValue();
    let finalEmail: string;
    let payloadEmail: string;

    if(formData.email?.trim()) {
      finalEmail = formData.email;
    } else {
      const [localPart, domain] = this.accountHolderEmail.split('@');
      finalEmail = `${localPart}+${formData.first_name.trim().toLowerCase()}@${domain}`;
    }
    payloadEmail = finalEmail.toLowerCase();

    const payload = {
      ...this.minorForm.getRawValue(),
      account_id: this.accountId,
      email: payloadEmail,
    };

    const createSub = this.accountService
      .createMinorAccount(payload)
      .subscribe({
        next: () => {
          this.isLoading = false;

          this.router
          .navigateByUrl(`/admin/accounts/individual/accounts/${this.accountId}`)
          .then(() => {
            window.location.reload();
            this.toast.success('Minor account created successfully');
          });
          this.change.detectChanges();
        },
        error: () => {
          this.isLoading = false;
          this.toast.error('Failed to create minor account');
          this.change.detectChanges();
        },
      });

    this.subs.push(createSub);
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}